import React, {
	forwardRef,
	useRef,
	useState,
	useCallback,
	type FocusEvent,
	type ReactNode,
} from 'react';
import { styled } from '@compiled/react';
import FocusLock from 'react-focus-lock';
import { layers } from '@atlassian/jira-common-styles/src';
import { CONTEXT_MENU_INNER_CONTAINER_TESTID } from '../../../../common/constants';
import { MenuContainer } from '../../../../common/ui';
import { useIsPersistentFocus } from '../../../../controllers/context-menu';

type WrapperDivProps = {
	onFocus: (e: FocusEvent<HTMLDivElement>) => void;
	ref: React.MutableRefObject<HTMLDivElement | null>;
	children: ReactNode;
};

type FocusLockedComponentProps = {
	children: ReactNode;
};

const WrapperDiv = forwardRef(
	({ onFocus, children }: WrapperDivProps, ref: React.ForwardedRef<HTMLDivElement | null>) => (
		<ContextMenuInnerContainer
			ref={ref}
			onFocus={onFocus}
			data-testid={CONTEXT_MENU_INNER_CONTAINER_TESTID}
		>
			{children}
		</ContextMenuInnerContainer>
	),
);

export const FocusLockedWrapper = ({ children }: FocusLockedComponentProps) => {
	const isPersistentFocus = useIsPersistentFocus();
	const [focuslockDisabled, setFocuslockDisabled] = useState(!isPersistentFocus);
	const ref = useRef<HTMLDivElement | null>(null);

	const onFocus = useCallback(
		(e: FocusEvent<HTMLDivElement>) => {
			if (focuslockDisabled && ref.current?.contains(e.target)) {
				setFocuslockDisabled(false);
			}
		},
		[focuslockDisabled],
	);

	return (
		<WrapperDiv onFocus={onFocus} ref={ref}>
			<FocusLock disabled={focuslockDisabled} persistentFocus={isPersistentFocus} autoFocus={false}>
				{children}
			</FocusLock>
		</WrapperDiv>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContextMenuInnerContainer = styled(MenuContainer)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: layers.layer,
	overflowX: 'visible',
	display: 'flex',
	flexDirection: 'column',
});
